/**
 * What the search box's "Ask AI" remembers for the rest of the session (whereis-8).
 *
 * Kept in memory only, per person per books, never in storage: a reload forgets
 * it, and nothing typed is written anywhere (spec section 7). It saves calls:
 * whose keys run here and whether the notice is agreed are read once; the
 * provider that answered is asked first next time; a question already answered
 * is answered again without asking anybody.
 *
 * `no_ai_key` stops further calls until Settings › AI is visited, which calls
 * {@link forgetAskAiSetup} (a key may have been added there, or the notice agreed).
 *
 * Lives outside `src/shared/whereis/` so the AI Settings page can clear it
 * without pulling the search chunk in.
 */

export type AskProvider = 'gemini' | 'claude';

export interface AskSetup {
    /** Providers with a key that work here runs on (the firm's or the business's own). */
    providers: string[];
    /** The firm whose key runs here, or null for the business's own. */
    firm: { id: string; name: string | null } | null;
    /** Someone has agreed to the current key notice for the key's business. */
    agreed: boolean;
}

export interface AskMemory {
    setup?: AskSetup;
    /** A provider said there is no key: no more calls until Settings › AI. */
    noKey?: boolean;
    /** The provider that answered last. */
    provider?: AskProvider;
    /** Question as sent → the place id it found, or null for "none". */
    answers: Map<string, string | null>;
}

const memory = new Map<string, AskMemory>();

/** The memory for `<user>:<books>`, made on first use. */
export function askMemory(key: string): AskMemory {
    let m = memory.get(key);
    if (!m) {
        m = { answers: new Map() };
        memory.set(key, m);
    }
    return m;
}

/** Settings › AI was visited: read the keys and the notice again on the next press. */
export function forgetAskAiSetup(): void {
    for (const m of memory.values()) {
        m.setup = undefined;
        m.noKey = false;
    }
}

/** Signed out: forget everything. */
export function forgetAskAiMemory(): void {
    memory.clear();
}
